import { Component, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs';
import { NavigationEnd } from '@angular/router';
import { resolvePageTitle, buildDocumentTitle } from '@/routes/path-titles';
import { APP_ROUTES } from '@/routes/routes.type';
import { BlogNavStore } from '@/store/blog-nav.store';
import { AnalyticsConsentBannerComponent } from '@/components/analytics-consent-banner/analytics-consent-banner.component';
import { MetrikaNoscriptComponent } from '@/components/metrika-noscript/metrika-noscript.component';
import { MetrikaService } from '@/services/metrika/metrika.service';
import { buildMetrikaPageUrl, isAdminPath } from '@/services/metrika/metrika.utils';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.css',
  standalone: true,
  imports: [RouterOutlet, MetrikaNoscriptComponent, AnalyticsConsentBannerComponent],
})
export class App {
  readonly #platformId = inject(PLATFORM_ID);
  readonly #router = inject(Router);
  readonly #title = inject(Title);
  readonly #blogNavStore = inject(BlogNavStore);
  readonly #metrika = inject(MetrikaService);

  readonly #isBrowser = isPlatformBrowser(this.#platformId);

  #previousUrl = '';
  #lastTrackedPageUrl: string | null = null;

  constructor() {
    void this.#blogNavStore.loadNavigation();
    this.#updateTitle();

    if (this.#isBrowser) {
      this.#initMetrikaForCurrentUrl();
    }

    this.#router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        this.#updateTitle();
        if (this.#isBrowser) {
          this.#handleMetrikaNavigation(event.urlAfterRedirects);
        }
      });
  }

  #initMetrikaForCurrentUrl(): void {
    const urlAtStart = this.#router.url;
    void this.#metrika.init().then(() => {
      this.#trackPageView(urlAtStart);
    });
  }

  #handleMetrikaNavigation(url: string): void {
    void this.#metrika.init().then(() => {
      this.#trackPageView(url);
    });
  }

  #trackPageView(url: string): void {
    if (isAdminPath(url)) {
      this.#metrika.destruct();
      this.#previousUrl = url;
      this.#lastTrackedPageUrl = null;
      return;
    }

    const pageUrl = buildMetrikaPageUrl(url, window.location.origin);
    if (pageUrl === this.#lastTrackedPageUrl) {
      this.#previousUrl = url;
      return;
    }

    const referer =
      this.#previousUrl && !isAdminPath(this.#previousUrl)
        ? buildMetrikaPageUrl(this.#previousUrl, window.location.origin)
        : undefined;

    this.#metrika.hit(pageUrl, {
      title: document.title,
      referer,
    });
    this.#previousUrl = url;
    this.#lastTrackedPageUrl = pageUrl;
  }

  #updateTitle(): void {
    const path = this.#router.url.split('?')[0];
    const pageTitle = resolvePageTitle(path);
    if (pageTitle === null && this.#isBlogDetailPath(path)) {
      return;
    }
    this.#title.setTitle(buildDocumentTitle(pageTitle));
  }

  #isBlogDetailPath(path: string): boolean {
    const normalized = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
    const categoryChild = APP_ROUTES.BLOG_CATEGORY_CHILD.split('/:')[0];
    const postChild = APP_ROUTES.BLOG_POST_CHILD.split('/:')[0];
    const prefix = `/${APP_ROUTES.BLOG}/`;
    return (
      normalized.startsWith(prefix) &&
      (normalized.includes(`/${categoryChild}/`) || normalized.includes(`/${postChild}/`))
    );
  }
}
